import React, { useState, useMemo } from "react";
import { useApp } from "../store";
import { ArrowRight, Plus, Package, Battery, Weight, Shield, Wrench, CreditCard } from "lucide-react";
import { getFaqData } from "../data/faqData";

const iconMap = {
  Package: Package,
  Battery: Battery,
  Weight: Weight,
  Shield: Shield,
  Wrench: Wrench,
  CreditCard: CreditCard,
};

export default function FAQ() {
  const { t, locale } = useApp();
  const [selected, setSelected] = useState(0);
  const [expanded, setExpanded] = useState({});

  const { categories } = getFaqData(locale);
  const current = categories[selected];

  // Build FAQPage schema.org from all categories
  const faqSchema = useMemo(() => {
    const allFaqs = categories.flatMap((cat) =>
      cat.faqs.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      }))
    );
    if (allFaqs.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFaqs,
    };
  }, [categories]);

  const toggle = (idx) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const CategoryIcon = iconMap[current.icon] || Package;

  return (
    <main className="pt-32 pb-32 bg-[#f6f6f3] min-h-screen" data-testid="faq-page">
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Hero */}
        <div className="mb-16 text-center">
          <div className="overline text-sky-500 mb-4 tracking-wider">{t.faqPage.helpCenter}</div>
          <h1 className="font-serif-display text-5xl md:text-7xl text-[#0b111e] mb-6">
            {t.faqPage.title}
          </h1>
          <p className="text-[#0b111e]/70 max-w-3xl mx-auto text-lg leading-relaxed">
            {t.faqPage.subtitle}
          </p>
        </div>

        {/* Hero CTAs */}
        <section className="mb-16 flex flex-wrap justify-center gap-4">
          <a href="/support" className="btn-primary">
            {t.faqPage.talkSpecialist} <ArrowRight size={16} />
          </a>
          <a href="/products" className="btn-primary">
            {t.faqPage.viewModels} <ArrowRight size={16} />
          </a>
          <a href="/contact" className="btn-primary">
            {t.faqPage.goContact} <ArrowRight size={16} />
          </a>
        </section>

        {/* Category chips with icons */}
        <div className="mb-10 flex flex-wrap justify-center gap-3">
          {categories.map((c, i) => {
            const Icon = iconMap[c.icon] || Package;
            return (
              <button
                key={i}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 cursor-pointer transition-colors ${
                  i === selected ? "bg-sky-500 text-white" : "bg-white text-[#0b111e]/80 hover:bg-[#f6f6f3] border border-[#e5e7eb]"
                }`}
                onClick={() => {
                  setSelected(i);
                  setExpanded({});
                }}
              >
                <Icon size={16} />
                {c.name}
              </button>
            );
          })}
        </div>

        {/* Category section with icon + title + subtitle */}
        <section className="mb-20">
          <div className="flex items-center justify-center gap-3 mb-4">
            <CategoryIcon size={28} className="text-sky-500" />
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#0b111e]">
              {current.name}
            </h2>
          </div>
          <h3 className="text-center text-[#0b111e]/60 mb-10">{current.subtitle}</h3>

          {/* FAQ cards: light theme, thin border, rounded, plus icon right */}
          <div className="space-y-4 max-w-4xl mx-auto">
            {current.faqs.map((item, idx) => (
              <div
                key={idx}
                className="border border-[#e5e7eb] rounded-xl bg-white overflow-hidden transition-colors"
              >
                <button
                  className="w-full text-left px-6 py-5 flex justify-between items-center gap-4 cursor-pointer"
                  onClick={() => toggle(idx)}
                >
                  <span className="font-semibold text-lg text-[#0b111e]">{item.q}</span>
                  <Plus
                    size={20}
                    className={`text-sky-500 flex-shrink-0 transition-transform duration-300 ${
                      expanded[idx] ? "rotate-45" : ""
                    }`}
                  />
                </button>
                {expanded[idx] && (
                  <div className="px-6 pb-5 pt-0">
                    <p className="text-[#0b111e]/70 text-sm leading-relaxed border-t border-[#e5e7eb] pt-4">
                      {item.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Light CTA block in Portuguese */}
        <section className="text-center bg-white py-12 px-6 rounded-2xl border border-[#e5e7eb]">
          <h3 className="text-2xl font-semibold mb-4 text-[#0b111e]">{t.faqPage.helpTitle}</h3>
          <p className="text-[#0b111e]/60 mb-6 max-w-2xl mx-auto">
            {t.faqPage.helpBody}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/support" className="btn-primary">
              {t.faqPage.talkSpecialist} <ArrowRight size={16} />
            </a>
            <a href="/products" className="btn-secondary">
              {t.faqPage.verModelos} <ArrowRight size={16} />
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}
